<script setup lang="ts">
  import GenericDialog from '../dialog/GenericDialog.vue'
  import GICollectionColorField from '@/components/form/GICollectionColorField.vue'
  import { BlockBlobClient } from '@azure/storage-blob'

  defineProps({
    label: { type: String, default: 'Upload tif file' },
    accept: { type: String, default: '.tif' }
  })
  const rasterFileUploadStore = useRasterFileUploadStore()
  const { addRasterFileDialog } = storeToRefs(rasterFileUploadStore)

  const projectStore = useProjectStore()
  const { currentItem } = storeToRefs(rasterFileUploadStore)
  const { currentItem: currentProject } = storeToRefs(projectStore)
  const isEditing = computed(() => {
    return !!currentItem.value['id']
  })
  const loading = ref(false)
  const progressUpload = ref(0)
  const computedProgressUpload = computed(() => {
    return Math.round(progressUpload.value)
  })
  const isLoaded = ref(false)
  const files = ref([])
  const collectionName = ref('')
  const rasterColor = ref('#000000')
  const rules = {
    required: (value: string) => !!value || 'Name is Required.'
  }

  const handleRaster = async (action: string) => {
    const formData = new FormData()
    formData.append('name', collectionName.value)
    formData.append('raster_color', rasterColor.value)
    const _file = files.value

    try {
      if (action === 'update') {
        await rasterFileUploadStore.update({
          id: currentItem.value.id,
          name: collectionName.value,
          raster_color: rasterColor.value,
          project: currentProject.value.id,
          source_file_name: currentItem.value.source_file_name
        })
        rasterFileUploadStore.setCurrentItem({})
        rasterFileUploadStore.toggleAddRasterFileDialog()
      } else if (action === 'upload') {
        loading.value = true
        isLoaded.value = false
        const uploadUrl = await projectStore.fetchUploadUrl(_file.name)
        const blockBlobClient = new BlockBlobClient(uploadUrl)
        await blockBlobClient.uploadData(files.value, {
          onProgress: (ev) => {
            progressUpload.value = (ev.loadedBytes / _file.size) * 100
          }
        })
        await rasterFileUploadStore.createRasterFileUpload(
          blockBlobClient.name,
          collectionName.value,
          rasterColor.value
        )
        rasterFileUploadStore.toggleAddRasterFileDialog()
        loading.value = false
        isLoaded.value = true
      }
    } catch (error) {
      console.log(error)
      reset()
    }
  }

  function reset() {
    isLoaded.value = false
    loading.value = false
  }

  onMounted(() => {
    collectionName.value = currentItem?.value?.name || ''
    rasterColor.value = currentItem?.value?.raster_color || '#000000'
  })
</script>

<template>
  <GenericDialog
    v-model="addRasterFileDialog"
    :max-width="600"
    @click:outside="rasterFileUploadStore.toggleAddRasterFileDialog"
  >
    <template #title> Upload file </template>
    <template #default>
      <v-text-field
        color="primary"
        label="Raster File Name"
        v-model="collectionName"
        type="text"
        :clearable="true"
        :persistent-placeholder="true"
        :rules="[rules.required]"
      ></v-text-field>
      <v-file-input
        v-if="!isEditing"
        color="primary"
        :disabled="loading"
        show-size
        prepend-icon="mdi-file"
        :label="label"
        :accept="accept"
        v-model="files"
        hide-details
        @click:clear="reset"
      >
      </v-file-input>
      <div class="d-flex flex-column mt-5 ga-4">
        <GICollectionColorField
          v-model="rasterColor"
          tooltipText="Select Color for the Raster File"
        />
        <v-card-subtitle class="text-subtitle text-wrap">
          <v-icon>mdi-information-outline</v-icon>
          The color you chose will be the color of the elevation line in the graph.
        </v-card-subtitle>
      </div>
      <div v-if="loading">
        <v-progress-linear :value="progressUpload"></v-progress-linear>
        <small>Uploading file to server {{ computedProgressUpload }}%...</small>
      </div>
    </template>
    <template #actions>
      <v-btn
        color="primary"
        variant="text"
        @click="rasterFileUploadStore.toggleAddRasterFileDialog"
      >
        Cancel
      </v-btn>
      <v-btn
        :disabled="
          isEditing ? collectionName.length <= 0 : !files || collectionName.length <= 0 || isLoaded
        "
        color="primary"
        :loading="loading"
        @click="isEditing ? handleRaster('update') : handleRaster('upload')"
      >
        Upload
      </v-btn>
    </template>
  </GenericDialog>
</template>
